import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useMutation, useQueryClient } from 'react-query';

import { useToast } from '@hooks';
import * as yup from 'yup';
import { DateInput } from '@components/DateInput/DateInput';
import { addUpdatedUpdate } from '@store/redux/features/updatesOptimistic';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { patchDueDate } from '@services/queries/updates/patchDueDate/patchDueDate';

import { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavigationContext } from '@layout/navigation/navigation';
import { useParams } from 'react-router-dom';
import { IUpdateRawQueryList } from '@models/Update.model';
import { addBackUpUpdatedUpdates } from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { getStableNow } from '@utils/getStableNow';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
} from './SetDueDateDialog.styles';

interface ISetDueDateDialog {
  open: boolean;
  onClose: () => void;
  updateStatusId?: string;
  updateId?: string;
  closeOnFinish?: boolean;
  updateStatus: IUpdateRawQueryList;
}

export interface IPiorityForm {
  date: Date | null;
}

const schema = yup.object({
  date: yup.date(),
});

export const SetDueDateDialog = ({
  open,
  onClose,
  updateStatusId,
  updateStatus,
  updateId,
  closeOnFinish = true,
}: ISetDueDateDialog) => {
  const portalDiv = document.getElementById('portal');

  const { updatesMtViewTime } = useContext(NavigationContext);
  const { toast } = useToast();
  const [dueDate, setDueDate] = useState<string | null>(
    updateStatus.dueDate?.toJSON() ?? null,
  );
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const params = useParams<{ id: string; equipmentId: string }>();

  const reloadQueries = async () => {
    queryClient.invalidateQueries(['updateStatuses', updateId]);
    queryClient.invalidateQueries(['updateStatuses']);
    queryClient.invalidateQueries(['updatesMetrics']);
    queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
    queryClient.invalidateQueries([
      'equipments',
      params.equipmentId,
      'updateStatuses',
    ]);
  };

  const dueDateMutation = useMutation(patchDueDate, {
    onSuccess: async newUpdate => {
      if (closeOnFinish) {
        onClose();
      }
      toast.success('Update changed successfully');
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));
      reloadQueries();
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const { control } = useForm<IPiorityForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      date: updateStatus.dueDate,
    },
  });

  const now = getStableNow();
  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Set Due Date</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />

            <Label>Set Due Date</Label>
            <Controller
              control={control}
              defaultValue={null}
              name="date"
              render={({ field: { onChange, value } }) => (
                <DateInput
                  placeholder="Choose..."
                  height={42}
                  width={331}
                  borderColor="#d8dae5"
                  value={value}
                  minDate={now.toISOString()}
                  handleDateChange={(e: any) => {
                    onChange(new Date(e));
                    setDueDate(e);
                  }}
                />
              )}
            />

            <Form>
              <Line>
                <Confirm
                  type="button"
                  disabled={dueDateMutation.isLoading || !dueDate}
                  onClick={() => {
                    if (!dueDateMutation.isLoading) {
                      dueDateMutation.mutate({
                        id: updateId,
                        dueDate: new Date(dueDate as string),
                        updateStatusId,
                      });
                    }
                  }}
                >
                  Complete
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
